import { Component, OnChanges, SimpleChanges, input, output } from '@angular/core';

/**
 * Section setup — shape consumed by `<ft-sections>` / `<ft-section>`.
 * Mirrors `ISectionSetup` from the AngularJS source.
 */
export interface SectionSetup {
  isActive: boolean;
  name: string;
  title: string;
  isDisabled?: boolean;
}

/**
 * Native-Angular port of the AngularJS `ftSections` directive
 * (account-v2/.../navigation/sections/sections.directive.ts). Holds a
 * registry of named sections + the active one; child `<ft-section>` /
 * `[ftSectionTrigger]` look up the parent via @Host() injection.
 *
 * Existing legacy template usage that reads `$ftSections.isSectionActive(...)`
 * doesn't translate to Angular's binding model — children should bind via a
 * template-ref var instead:
 *   <ft-sections #ftSecs [registry]="sections" (afterInit)="onInit($event)">
 *     <li [class.active]="ftSecs.isSectionActive('current')">
 *       <a ftSectionTrigger="current">Current</a>
 *     </li>
 *     <ft-section name="current">...content...</ft-section>
 *   </ft-sections>
 *
 * Provided by `AccountSharedModule` so any native Angular leaf migrated off
 * the AngularJS shell can reuse the tab/section model without bridging the
 * original directive (its `scope: false` + `scope.$eval(attrs.registry)` +
 * `$ftSections`-on-parent-scope pattern fights Angular content projection).
 */
@Component({
    selector: 'ft-sections',
    template: '<ng-content />',
    standalone: false
})
export class FtSectionsComponent implements OnChanges {
    readonly registry = input<SectionSetup[]>([]);
    /** Fires after the registry first lands; payload mirrors v1's `{registry, controller}`. */
    readonly afterInit = output<{ registry: SectionSetup[]; controller: FtSectionsComponent }>();

    private initialized = false;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['registry'] && this.registry() && !this.initialized) {
            this.initialized = true;
            // queueMicrotask to avoid expression-changed-after-checked when the
            // parent reads back into its own template in the same CD cycle.
            Promise.resolve().then(() => this.afterInit.emit({ registry: this.registry(), controller: this }));
        }
    }

    activate(section: SectionSetup | string): void {
        const target = typeof section === 'string'
            ? this.registry().find((rs) => rs.name === section)
            : section;
        if (!target) return;
        for (const rs of this.registry()) rs.isActive = false;
        target.isActive = true;
    }

    isSectionActive(sectionName: string): boolean {
        if (!this.registry()?.length) return false;
        const section = this.registry().find((rs) => rs.name === sectionName);
        return !!section?.isActive;
    }

    getActiveSection(): SectionSetup | undefined {
        return this.registry()?.find((s) => s.isActive);
    }
}
